import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { Component, ChangeDetectionStrategy, Input, OnInit, Output, EventEmitter, OnDestroy, inject, DOCUMENT } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { LinkService } from '@shared/service/link.service';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-pagination',
    templateUrl: './pagination.component.html',
    styleUrls: ['./pagination.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LinkService],
    imports: [RouterLink, AsyncPipe, TranslatePipe],
})
export class PaginationComponent implements OnInit, OnDestroy {
    private activatedRoute = inject(ActivatedRoute);
    private router = inject(Router);
    private linkService = inject(LinkService);
    private document = inject(DOCUMENT);

    @Input({ required: true }) total!: number;
    @Input({ required: true }) limit!: number;

    @Output() pageChanged = new EventEmitter<number>();
    currentPage$ = this.linkService.currentPage$;

    private subscriptions$ = new Subscription();

    ngOnInit(): void {
        this.activatedRoute.params.subscribe((params: Params) => {
            this.linkService.preparePageNumber(undefined, params);
        });

        this.subscriptions$.add(
            this.currentPage$
                .pipe(
                    tap((page) => {
                        page = page < 1 ? 1 : page;
                        void this.router.navigate(['..', page], {
                            relativeTo: this.activatedRoute,
                            queryParamsHandling: 'merge',
                        });

                        const domain = `${this.document.location.protocol}//${this.document.location.hostname}`;
                        const path = this.router.createUrlTree(['..'], { relativeTo: this.activatedRoute }).toString();
                        const url = `${domain}${path}`;

                        const prevPage = page > 1 ? true : false;
                        const nextPage = page < this.totalPages ? true : false;

                        void this.linkService.createOrUpdatePrevTag(prevPage, page, url);
                        void this.linkService.createOrUpdateNextTag(nextPage, page, url);
                    }),
                )
                .subscribe(),
        );

        this.subscriptions$.add(
            this.activatedRoute.params
                .pipe(
                    map((value) => parseInt(value['pageNumber'])),
                    filter((value) => !isNaN(value)),
                    tap((page) => {
                        this.pageChanged.emit(page);
                    }),
                )
                .subscribe(),
        );
    }

    ngOnDestroy(): void {
        this.linkService.cleanUp();
        this.subscriptions$.unsubscribe();
    }

    getMaxPages(totalItems: number, limit: number): number {
        return Math.ceil(totalItems / limit);
    }

    isValidPageNumber(page: number, totalPages: number): boolean {
        return page > 0 && page <= totalPages;
    }

    get totalPages(): number {
        return this.getMaxPages(this.total, this.limit);
    }

    get canGoPrevPage(): boolean {
        return this.currentPage$.value > 1;
    }

    get canGoNextPage(): boolean {
        return this.currentPage$.value < this.totalPages;
    }

    get prevPageNumber(): number {
        return this.currentPage$.value - 1;
    }

    get nextPageNumber(): number {
        return this.currentPage$.value + 1;
    }
}
